import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { nectarApi } from '@/features/nectar/api/client'
import { parseMoney } from '@/utils'
import type { LedgerEntry, LedgerEnvelope, LedgerKind } from './models'

type LedgerEntryWire = Omit<LedgerEntry, 'amountUsd'> & { amountUsd: string }

type LedgerEnvelopeWire = {
  data: LedgerEntryWire[]
  meta: LedgerEnvelope['meta']
}

const hasInFlightPayout = (envelope: LedgerEnvelope | undefined): boolean => {
  if (!envelope) return false
  return envelope.data.some((row) => {
    const status = row.payout?.status
    return status === 'REQUESTED' || status === 'PROCESSING'
  })
}

type UseFetchLedgerArgs = {
  kind?: LedgerKind
  page: number
  limit: number
}

export const useFetchLedger = ({ kind, page, limit }: UseFetchLedgerArgs): UseQueryResult<LedgerEnvelope> => {
  return useQuery({
    queryKey: ['ledger', { kind, page, limit }],
    queryFn: async (): Promise<LedgerEnvelope> => {
      const wire = await nectarApi.get<LedgerEnvelopeWire>('/v1/user/balance/ledger', { kind, page, limit })
      return {
        data: wire.data.map((row) => ({
          ...row,
          amountUsd: parseMoney(row.amountUsd)
        })),
        meta: wire.meta
      }
    },
    staleTime: 30_000,
    refetchInterval: (query) => (hasInFlightPayout(query.state.data) ? 5_000 : false)
  })
}
